import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/gate-check/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Buildings, Plus, Trash } from "@phosphor-icons/react";

export default function AdminGates() {
    const [gates, setGates] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", location: "", address: "" });

    const load = async () => setGates((await api.get("/gate-check/gates")).data);
    useEffect(() => { load(); }, []);

    const add = async () => {
        try {
            await api.post("/gate-check/gates", form);
            toast.success("Gate added");
            setForm({ name: "", location: "", address: "" });
            setOpen(false); load();
        } catch { toast.error("Failed"); }
    };

    const remove = async (id) => { await api.delete(`/gate-check/gates/${id}`); toast.success("Removed"); load(); };

    return (
        <div>
            <PageHeader
                overline="Admin"
                title="Gates & Locations"
                subtitle="Configure gates across all BBS Global sites."
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="btn-add-gate" className="h-11 bg-brand hover:bg-brand/90 text-white">
                                <Plus size={16} weight="bold" className="mr-2" /> Add Gate
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle className="font-display">Add Gate / Location</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <div>
                                    <Label className="overline text-[10px]">Gate Name *</Label>
                                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        data-testid="input-gate-name" className="h-11 mt-1" />
                                </div>
                                <div>
                                    <Label className="overline text-[10px]">Location</Label>
                                    <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                                        data-testid="input-gate-location" className="h-11 mt-1" />
                                </div>
                                <div>
                                    <Label className="overline text-[10px]">Address</Label>
                                    <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                                        data-testid="input-gate-address" className="h-11 mt-1" />
                                </div>
                                <Button onClick={add} data-testid="btn-save-gate"
                                    className="w-full h-11 bg-brand hover:bg-brand/90 text-white">Save Gate</Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                }
            />
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {gates.map((g) => (
                    <Card key={g.id} className="tech-card p-5" data-testid={`card-gate-${g.id}`}>
                        <div className="flex items-start justify-between">
                            <Buildings size={24} weight="duotone" className="text-brand" />
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(g.id)}
                                data-testid={`btn-remove-gate-${g.id}`}>
                                <Trash size={14} />
                            </Button>
                        </div>
                        <div className="mt-3 font-display font-black text-xl tracking-tight">{g.name}</div>
                        <div className="text-xs text-muted-foreground mt-1">{g.location}</div>
                        <div className="text-xs mt-2">{g.address}</div>
                    </Card>
                ))}
                {gates.length === 0 && (
                    <div className="col-span-full p-8 text-center text-muted-foreground">No gates configured.</div>
                )}
            </div>
        </div>
    );
}
